import { mm, TOLERANCE, type Um } from './units.js';
import type { HardwareItem, Issue, PanelPart, Vec3 } from './model.js';
import type { AssemblyInstance } from './document.js';
import { makePanel, partBox } from './geometry.js';
import { panelSize } from './assembly.js';

/**
 * Plinth and fillers of an assembly ("Cokla" below, "Blenda" on top and on the sides). Each is a
 * front-facing board just outside the carcass box, with its own size (height of the plinth / top
 * filler, width of a side filler) and depth position (setback of its front face behind the front of
 * the assembly).
 *
 * Mode "add": the board is added to the assembly (the outer size grows; a plinth lifts the assembly
 * so it still stands where it stood). Mode "shrink": the outer size stays and the carcass gives the
 * room — panels on that side move in or get shorter (shelves, drawers and fronts follow the panels).
 */

export type TrimSide = 'bottom' | 'top' | 'left' | 'right';
export const TRIM_SIDES: TrimSide[] = ['bottom', 'top', 'left', 'right'];

export interface AssemblyTrim {
  /** Height (bottom / top) or width (left / right). */
  size: Um;
  mode: 'add' | 'shrink';
  /** Front face of the board behind the front of the assembly (fronts included); negative = in front. */
  setback: Um;
  thickness: Um;
}

export const TRIM_LABEL: Record<TrimSide, string> = { bottom: 'Cokla', top: 'Blenda gore', left: 'Blenda lijevo', right: 'Blenda desno' };
export const TRIM_KEY: Record<TrimSide, string> = { bottom: 'cokla', top: 'blenda-gore', left: 'blenda-lijevo', right: 'blenda-desno' };

export function defaultTrim(side: TrimSide): AssemblyTrim {
  return side === 'bottom' ? { size: mm(100), mode: 'add', setback: mm(50), thickness: mm(18) } : { size: mm(50), mode: 'add', setback: 0, thickness: mm(18) };
}

export function trimOfPart(inst: AssemblyInstance, localKey: string): TrimSide | null {
  return TRIM_SIDES.find((s) => inst.trims?.[s] && TRIM_KEY[s] === localKey) ?? null;
}

const AXIS = { bottom: 1, top: 1, left: 0, right: 0 } as const;
const LOW = { bottom: true, top: false, left: true, right: false } as const;

/**
 * Gives (d > 0) or takes back (d < 0) room on one side of the carcass: panels lying across that side
 * (their thickness along the axis) move in, panels running to it get shorter.
 */
export function shrinkCarcass(inst: AssemblyInstance, side: TrimSide, d: Um): void {
  if (!d || !inst.panels.length) return;
  const k = AXIS[side];
  const low = LOW[side];
  const ext = inst.panels.map((p) => {
    const s = panelSize(p);
    const n = (p.repeat && p.repeat.axis === 'xyz'[k] ? (p.repeat.count - 1) * p.repeat.spacing : 0) + s[k];
    return { p, s, lo: p.position[k], hi: p.position[k] + n };
  });
  const edge = low ? Math.min(...ext.map((e) => e.lo)) : Math.max(...ext.map((e) => e.hi));
  const tol = TOLERANCE.contact;
  const fieldOf = (p: AssemblyInstance['panels'][number]) => (['length', 'width', 'thickness'] as const).find((f) => sizeAxis(p, f) === k)!;
  // Panels lying across the side (thickness along the axis, or a row of copies along it) at the edge move in…
  const across = (e: (typeof ext)[number]) => fieldOf(e.p) === 'thickness' || e.p.repeat?.axis === 'xyz'[k];
  const moved = ext.filter((e) => across(e) && (low ? e.lo <= edge + tol : e.hi >= edge - tol));
  // …and panels running to the edge or to those panels (e.g. a bottom between the sides) get shorter.
  const inner = moved.length ? (low ? Math.max(...moved.map((e) => e.hi)) : Math.min(...moved.map((e) => e.lo))) : edge;
  for (const e of ext) {
    const p = e.p;
    if (moved.includes(e)) {
      p.position[k] += low ? d : -d;
      continue;
    }
    if (across(e) || (low ? e.lo > inner + tol : e.hi < inner - tol)) continue;
    p[fieldOf(p)] -= d;
    if (low) p.position[k] += d;
  }
}

function sizeAxis(p: AssemblyInstance['panels'][number], f: 'length' | 'width' | 'thickness'): number {
  const axes = { horizontal: ['x', 'z', 'y'], 'vertical-side': ['y', 'z', 'x'], 'vertical-front': ['x', 'y', 'z'] }[p.orientation];
  return 'xyz'.indexOf(axes[['length', 'width', 'thickness'].indexOf(f)]!);
}

/**
 * How far the assembly stands above its old floor line: the room under the carcass (the taller of the
 * plinth and the legs) minus what a "shrink" plinth already took from the carcass.
 */
function liftOf(inst: AssemblyInstance): number {
  const p = inst.trims?.bottom;
  const under = Math.max(p?.size ?? 0, inst.legs?.height ?? 0);
  return under - (p?.mode === 'shrink' ? p.size : 0);
}

/**
 * Turns a plinth / filler on, changes it or turns it off (null), keeping the document consistent:
 * "shrink" moves the carcass by the change in size, "add" on the bottom lifts the assembly.
 */
export function setAssemblyTrim(inst: AssemblyInstance, side: TrimSide, next: AssemblyTrim | null): void {
  const prev = inst.trims?.[side];
  const lift0 = liftOf(inst);
  const shrinkOf = (t: AssemblyTrim | null | undefined) => (t?.mode === 'shrink' ? t.size : 0);
  shrinkCarcass(inst, side, shrinkOf(next) - shrinkOf(prev));
  const trims = { ...(inst.trims ?? {}) };
  if (next) trims[side] = next;
  else delete trims[side];
  if (Object.keys(trims).length) inst.trims = trims;
  else delete inst.trims;
  inst.transform.position[1] += liftOf(inst) - lift0;
}

// ---------- legs ----------

/**
 * Legs ("nogice") under the carcass. Pattern "auto": the four corners, under every vertical divider
 * standing on the bottom, and more legs wherever two neighbours (across or in depth) are further apart
 * than `maxSpacing`. Pattern "corners": the four corners only. The legs lift the assembly (they share
 * the room under the carcass with a plinth; the taller one counts) and stand behind a plinth.
 */
export interface AssemblyLegs {
  height: Um;
  pattern: 'auto' | 'corners';
  maxSpacing: Um;
  /** Leg centre from the carcass edges. */
  inset: Um;
  diameter: Um;
}

export const LEG_KEY = 'nogica';

export function defaultLegs(inst: AssemblyInstance): AssemblyLegs {
  return { height: inst.trims?.bottom?.size ?? mm(100), pattern: 'auto', maxSpacing: mm(600), inset: mm(50), diameter: mm(40) };
}

export function isLegPart(localKey: string): boolean {
  return localKey.startsWith(`${LEG_KEY}-`);
}

export function setAssemblyLegs(inst: AssemblyInstance, next: AssemblyLegs | null): void {
  const lift0 = liftOf(inst);
  if (next) inst.legs = next;
  else delete inst.legs;
  inst.transform.position[1] += liftOf(inst) - lift0;
}

/** Positions along one direction: merges near ones and fills gaps wider than `max` evenly. */
function spread(values: number[], max: number): number[] {
  const v = [...values].sort((a, b) => a - b).filter((x, i, a) => i === 0 || x - a[i - 1]! > mm(100));
  const out: number[] = [];
  for (let i = 0; i < v.length; i++) {
    out.push(v[i]!);
    const gap = i + 1 < v.length ? v[i + 1]! - v[i]! : 0;
    const n = Math.ceil(gap / max) - 1;
    for (let k = 1; k <= n; k++) out.push(v[i]! + (gap * k) / (n + 1));
  }
  return out.map((x) => Math.round(x));
}

/** Leg positions (centres in the assembly plane x/z) and the carcass bottom they stand under. */
export function legLayout(inst: AssemblyInstance, parts: PanelPart[]): { xs: number[]; zs: number[]; bottom: number } | null {
  const legs = inst.legs;
  const body = parts.filter((p) => p.role !== 'door');
  if (!legs || !body.length) return null;
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const p of body) {
    const b = partBox(p);
    for (const k of [0, 1, 2] as const) {
      min[k] = Math.min(min[k], b.min[k]);
      max[k] = Math.max(max[k], b.min[k] + b.size[k]);
    }
  }
  const front = Math.max(...parts.map((p) => partBox(p).min[2] + partBox(p).size[2]));
  const r = legs.diameter / 2;
  const inset = Math.max(legs.inset, r);
  const edge = (lo: number, hi: number) => (hi - lo > 2 * inset ? [lo + inset, hi - inset] : [(lo + hi) / 2]);
  let [zBack, zFront] = edge(min[2], max[2]) as [number, number?];
  zFront ??= zBack;
  // Behind the plinth board (10 mm free).
  const plinth = inst.trims?.bottom;
  if (plinth) zFront = Math.min(zFront, front - plinth.setback - plinth.thickness - r - mm(10));
  if (zFront < zBack) zFront = zBack;
  let xs = edge(min[0], max[0]);
  let zs = zFront - zBack > mm(100) ? [zBack, zFront] : [zFront];
  if (legs.pattern === 'auto') {
    // Under vertical dividers standing on the bottom (not the outer sides).
    for (const p of inst.panels) {
      if (p.orientation !== 'vertical-side' || p.repeat) continue;
      const c = p.position[0] + p.thickness / 2;
      if (c > min[0] + inset + mm(50) && c < max[0] - inset - mm(50) && p.position[1] <= min[1] + mm(60)) xs.push(c);
    }
    xs = spread(xs, legs.maxSpacing);
    zs = spread(zs, legs.maxSpacing);
  } else xs = xs.map((x) => Math.round(x));
  return { xs, zs: zs.map((z) => Math.round(z)), bottom: min[1] };
}

export function buildLegs(inst: AssemblyInstance, parts: PanelPart[]): { parts: PanelPart[]; hardware: HardwareItem[]; issues: Issue[] } {
  const legs = inst.legs;
  const issues: Issue[] = [];
  if (!legs) return { parts: [], hardware: [], issues };
  const err = (message: string) => issues.push({ severity: 'error', code: 'legs.invalid', message: `Nogice: ${message}`, cabinetId: inst.id });
  if (!Number.isInteger(legs.height) || legs.height <= 0 || legs.height > mm(500)) err('visina mora biti 1–500 mm.');
  if (!Number.isInteger(legs.maxSpacing) || legs.maxSpacing < mm(100) || legs.maxSpacing > mm(3000)) err('najveći razmak mora biti 100–3000 mm.');
  if (!Number.isInteger(legs.diameter) || legs.diameter <= 0 || legs.diameter > mm(200)) err('prečnik mora biti 1–200 mm.');
  if (!Number.isInteger(legs.inset) || legs.inset < 0 || legs.inset > mm(500)) err('odmak od ivice mora biti 0–500 mm.');
  const lay = issues.length ? null : legLayout(inst, parts);
  if (!lay) return { parts: [], hardware: [], issues };
  const r = legs.diameter / 2;
  const out: PanelPart[] = [];
  for (const z of lay.zs)
    for (const x of lay.xs) {
      const n = out.length + 1;
      out.push(
        makePanel({
          cabinetId: inst.id,
          localKey: `${LEG_KEY}-${n}`,
          role: 'custom',
          label: `Nogica ${n}`,
          box: { min: [x - r, lay.bottom - legs.height, z - r], size: [legs.diameter, legs.height, legs.diameter] },
          lengthAxis: 'y',
          widthAxis: 'x',
          materialSlot: 'carcass',
          grain: 'none',
          inCutList: false,
        }),
      );
    }
  const hardware: HardwareItem[] = out.length
    ? [{ id: `${inst.id}/${LEG_KEY}`, cabinetId: inst.id, kind: 'leg', label: 'Nogica (generička)', quantity: out.length, generic: true, note: `Visina ${legs.height / 1000} mm.` }]
    : [];
  return { parts: out, hardware, issues };
}

/** The boards, just outside the box of the other parts (fronts included). */
export function buildTrims(inst: AssemblyInstance, parts: PanelPart[], usedKeys: Set<string>): { parts: PanelPart[]; issues: Issue[] } {
  const trims = inst.trims;
  const out: PanelPart[] = [];
  const issues: Issue[] = [];
  if (!trims || !parts.length) return { parts: out, issues };
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const p of parts) {
    const b = partBox(p);
    for (const k of [0, 1, 2] as const) {
      min[k] = Math.min(min[k], b.min[k]);
      max[k] = Math.max(max[k], b.min[k] + b.size[k]);
    }
  }
  const L = trims.left?.size ?? 0;
  const R = trims.right?.size ?? 0;
  for (const side of TRIM_SIDES) {
    const t = trims[side];
    if (!t) continue;
    const label = TRIM_LABEL[side];
    const err = (message: string) => issues.push({ severity: 'error', code: 'trim.invalid', message: `${label}: ${message}`, cabinetId: inst.id });
    if (!Number.isInteger(t.size) || t.size <= 0 || t.size > mm(1000)) {
      err('mjera mora biti 1–1000 mm.');
      continue;
    }
    if (!Number.isInteger(t.thickness) || t.thickness <= 0 || t.thickness > mm(100)) {
      err('debljina mora biti 1–100 mm.');
      continue;
    }
    if (usedKeys.has(TRIM_KEY[side])) {
      err(`ključ "${TRIM_KEY[side]}" već koristi ploča.`);
      continue;
    }
    const zFront = max[2] - t.setback;
    const z0 = zFront - t.thickness;
    const horizontal = side === 'bottom' || side === 'top';
    const x0 = horizontal ? min[0] - L : side === 'left' ? min[0] - t.size : max[0];
    const x1 = horizontal ? max[0] + R : side === 'left' ? min[0] : max[0] + t.size;
    const y0 = side === 'bottom' ? min[1] - t.size : side === 'top' ? max[1] : min[1];
    const y1 = side === 'bottom' ? min[1] : side === 'top' ? max[1] + t.size : max[1];
    out.push(
      makePanel({
        cabinetId: inst.id,
        localKey: TRIM_KEY[side],
        role: side === 'bottom' ? 'plinth' : 'custom',
        label,
        box: { min: [x0, y0, z0], size: [x1 - x0, y1 - y0, t.thickness] },
        // Along the long side: plinth / top filler horizontally, side fillers vertically.
        lengthAxis: horizontal ? 'x' : 'y',
        widthAxis: horizontal ? 'y' : 'x',
        materialSlot: side === 'bottom' ? 'plinth' : 'front',
        grain: 'length',
      }),
    );
  }
  return { parts: out, issues };
}
